import { useEffect, useRef, useState } from "react";
import { Button, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import Editor from "@monaco-editor/react";
import DeviceTemplateApi from "../../../../../../api/DeviceTemplateApi";
import {
    DataParseSettingsWrapper,
    EditorHeader,
    EditorTitle,
    EditorDescription,
    EditorToolbar,
    EditorContainer
} from "./DataParseSettings.styled";

interface DataParseSettingsProps {
    deviceTemplateView: any
}

const DEFAULT_SCRIPT = `/**
 * 自定义数据解析脚本
 *
 * @param reader - 数据流读取器，可通过其提供的方法读取原始数据
 * @returns 解析后的数据对象，键为变量标识，值为解析后的值
 *
 * 示例:
 * function parseMqttDeviceMetrics(reader) {
 *     var uint8 = reader.readUint8(); // 读取一个字节
 *     var int8 = reader.readInt8(); // 小端序读取一个无符号短整型，可以使用readUint16Msb()替换为大端序读取
 *     var uin16 = reader.readUint16Lsb(); // 小端序读取一个浮点数，可以使用readFloat32Msb()替换为大端序读取
 *     var int16 = reader.readInt16Lsb(); // 同上，读取短整型
 *     var uint32 = reader.readUint32Lsb(); // 小端序读取一个无符号32位整数，可以使用readUint32Msb()替换为大端序读取
 *     var int32 = reader.readInt32Lsb(); // 同上，读取整型
 *     var uint64 = reader.readUint64Lsb(); // 小端序读取一个无符号64位整数，可以使用readUint64Msb()替换为大端序读取
 *     var int64 = reader.readInt64Lsb(); // 同上，读取64位整数
 *     var float32 = reader.readFloat32Lsb(); // 小端序读取一个浮点数，可以使用readFloat32Msb()替换为大端序读取
 *     var float64 = reader.readFloat64Lsb(); // 同上，读取双精度浮点数
 * 
 *     var pressure = reader.readFloat32Lsb(); // 小端序浮点数读取，可以使用readFloat32Msb()替换为大端序浮点数读取
 *     var temperature = reader.readFloat32Lsb(); // 同上，读取浮点数
 *     var timestamp = reader.readInt64Lsb(); // 小端序64位整数读取，可以使用readInt64Msb()替换为大端序64位整数读取
 *     var result = {
 *         'pressure': pressure,
 *         'temperature': temperature,
 *         'ts': timestamp
 *     };
 *     return result;
 * }
 * 
 * // 自定义二进制数据解析脚本示例：
 * function parseMqttBinaryDeviceMetrics(reader) {
 *     
 * }
 * 
 * function parseMqttHeartBeat(reader) {
 * 
 * }
 * 
 * function parseDataFileDeviceMetrics(reader) {
 * 
 * }
 */
`;

const DataParseSettings: React.FC<DataParseSettingsProps> = (props) => {
    let deviceTemplateView = props.deviceTemplateView;
    let deviceTemplateApi = new DeviceTemplateApi();
    const [messageApi, contextHolder] = message.useMessage();

    // state
    let [script, setScript] = useState("");
    let [hasChanges, setHasChanges] = useState(false);
    let [saving, setSaving] = useState(false);
    let editorRef = useRef<any>(null);

    // load script
    let loadScript = () => {
        let configurationJson = deviceTemplateView.deviceTemplate.configuration;
        let configuration = JSON.parse(configurationJson ? configurationJson : '{}');
        let dataParseSettings = configuration.dataParseSettings;

        if (dataParseSettings && dataParseSettings.messageParseScript) {
            setScript(dataParseSettings.messageParseScript);
        } else {
            setScript(DEFAULT_SCRIPT);
        }
    };

    // handle editor mounted
    let onEditorMount = (editor: any, monaco: any) => {
        editorRef.current = editor;
        editor?.layout();
    };

    // handle script change
    let onEditorChange = (value: string | undefined) => {
        if (value !== undefined) {
            setScript(value);
            setHasChanges(true);
        }
    };

    // save script
    let onSave = async () => {
        setSaving(true);
        try {
            // update configuration
            let configurationJson = deviceTemplateView.deviceTemplate.configuration;
            let configuration = JSON.parse(configurationJson ? configurationJson : '{}');

            if (!configuration.dataParseSettings) {
                configuration.dataParseSettings = {};
            }
            configuration.dataParseSettings.messageParseScript = script;
            deviceTemplateView.deviceTemplate.configuration = JSON.stringify(configuration);

            // do save
            await deviceTemplateApi.saveConfiguration(deviceTemplateView.deviceTemplate.id, configuration);

            messageApi.success("保存成功");
            setHasChanges(false);
        } catch (error) {
            console.error("Failed to save script:", error);
            messageApi.error("保存失败");
        }
        setSaving(false);
    };

    useEffect(() => {
        loadScript();
    }, []);

    return (
        <DataParseSettingsWrapper>
            {contextHolder}

            <EditorHeader>
                <div>
                    <EditorTitle>自定义数据解析脚本</EditorTitle>
                    <EditorDescription>
                        使用 JavaScript (ES5) 编写自定义数据解析逻辑
                    </EditorDescription>
                </div>
                <EditorToolbar>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={onSave}
                        loading={saving}
                        disabled={!hasChanges}
                    >
                        保存
                    </Button>
                </EditorToolbar>
            </EditorHeader>

            <EditorContainer>
                <Editor
                    height="100%"
                    defaultLanguage="javascript"
                    value={script}
                    onMount={onEditorMount}
                    onChange={onEditorChange}
                    theme="vs-dark"
                    options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 4,
                        insertSpaces: true,
                        wordWrap: 'on',
                        folding: true,
                        renderWhitespace: 'selection',
                        bracketPairColorization: { enabled: true },
                        suggestOnTriggerCharacters: true,
                        quickSuggestions: true,
                        parameterHints: true
                    }}
                />
            </EditorContainer>
        </DataParseSettingsWrapper>
    );
};

export default DataParseSettings;